'use client';

import { StatusBadge } from '@/components/common';
import { approveReleasePm, approveReleasePo } from '@/domain/products/api';
import { type Product, type RelatedIndustry } from '@/domain/products/types';
import { useAuth } from '@/hooks/useAuth';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { ToggleButton } from '@andritzot/metris-web-ui/inputs/toggle-button';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import styles from './ManagersSection.module.scss';

interface ManagersSectionProps {
  product: Product;
}

const managerTemplate = (rowData: RelatedIndustry, subject: string) => (
  <>
    <span>{rowData.manager?.name}</span>
    {rowData.manager?.email && (
      <>
        <span> - </span>
        <a
          href={`mailto:${rowData.manager?.email}?subject=${encodeURIComponent(subject)}`}
          className={styles.emailLink}
          target="_blank"
          rel="noreferrer"
        >
          {rowData.manager?.email}
        </a>
      </>
    )}
  </>
);

const ManagersSection = ({ product }: ManagersSectionProps) => {
  const [poApproved, setPoApproved] = useState(product.poApproved);
  const [pmApproved, setPmApproved] = useState(product.pmApproved);
  const { user } = useAuth();
  const managers = product.industries.filter((i) => !!i.manager);
  const { productOwner, productManager, technologyManager } = product;

  const handleReleasePo = useCallback(async (idProduct: number, status: boolean) => {
    try {
      await approveReleasePo(idProduct, status);
      setPoApproved(status);
      if (status) {
        toast.success('Release approved!');
      } else {
        toast.warning('Release back to pending!');
      }
    } catch {
      toast.error('Failed to approve release');
    }
  }, []);

  const handleReleasePm = useCallback(async (idProduct: number, status: boolean) => {
    try {
      await approveReleasePm(idProduct, status);
      setPmApproved(status);
      if (status) {
        toast.success('Release approved!');
      } else {
        toast.warning('Release back to pending!');
      }
    } catch {
      toast.error('Failed to approve release');
    }
  }, []);

  if (!!productOwner || !!productManager || !!technologyManager) {
    const subject = `Automation Solution Finder - ${product.product}`;
    return (
      <div className={styles.container}>
        <h4 className={styles.title}>Product Management</h4>
        {productOwner && (
          <div className={styles.listContent}>
            <p className={styles.title}>
              <b>Technical Product Owner</b>: <span>{productOwner.name}</span>
              {productOwner.email && (
                <>
                  <span> - </span>
                  <a
                    href={`mailto:${productOwner.email}?subject=${encodeURIComponent(subject)}`}
                    className={styles.emailLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {productOwner.email}
                  </a>
                </>
              )}
            </p>

            <div className="ml-2">
              {user?.idUser === productOwner.idUser ? (
                <Tooltip
                  title={
                    poApproved
                      ? 'This item has been approved for release'
                      : 'This item is pending your approval for release'
                  }
                >
                  <ToggleButton
                    value="release"
                    selected={poApproved}
                    onChange={async () => {
                      await handleReleasePo(product.idProduct, !poApproved);
                    }}
                  >
                    {poApproved ? 'Release Approved' : 'Pending Release'}
                  </ToggleButton>
                </Tooltip>
              ) : (
                <StatusBadge color={poApproved ? 'green' : 'yellow'}>
                  {poApproved ? 'released' : 'pending release'}
                </StatusBadge>
              )}
            </div>
          </div>
        )}
        {technologyManager && (
          <div className={styles.listContent}>
            <p className={styles.title}>
              <b>Head of Technology</b>: <span>{technologyManager.name}</span>
              {technologyManager.email && (
                <>
                  <span> - </span>
                  <a
                    href={`mailto:${technologyManager.email}?subject=${encodeURIComponent(subject)}`}
                    className={styles.emailLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {technologyManager.email}
                  </a>
                </>
              )}
            </p>
          </div>
        )}
        {productManager && (
          <div className={styles.listContent}>
            <p className={styles.title}>
              <b>Global Product Manager</b>: <span>{productManager.name}</span>
              {productManager.email && (
                <>
                  <span> - </span>
                  <a
                    href={`mailto:${productManager.email}?subject=${encodeURIComponent(subject)}`}
                    className={styles.emailLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {productManager.email}
                  </a>
                </>
              )}
            </p>
            <div className="ml-2">
              {user?.idUser === productManager.idUser ? (
                <Tooltip
                  title={
                    pmApproved
                      ? 'This item has been approved for release'
                      : 'This item is pending your approval for release'
                  }
                >
                  <ToggleButton
                    value="release"
                    selected={pmApproved}
                    onChange={async () => {
                      await handleReleasePm(product.idProduct, !pmApproved);
                    }}
                  >
                    {pmApproved ? 'Release Approved' : 'Pending Release'}
                  </ToggleButton>
                </Tooltip>
              ) : (
                <StatusBadge color={pmApproved ? 'green' : 'yellow'}>
                  {pmApproved ? 'released' : 'pending release'}
                </StatusBadge>
              )}
            </div>
          </div>
        )}

        {managers.length > 0 && (
          <DataTable value={managers} stripedRows showGridlines size="small" dataKey="idIndustry">
            <Column field="industry" header="Industry" style={{ width: '15rem' }} />
            <Column header="Manager" field="manager.name" body={(rowData: RelatedIndustry) => managerTemplate(rowData, subject)} />
          </DataTable>
        )}
      </div>
    );
  }
  return null;
};

export default ManagersSection;
